import {copyText} from '../contentGenerator/clipboard';
import {
    extractFormulaVarNames,
    resolveFormula,
    resolveVariableReferenceValue,
    resolveVars,
    tokenizeFormulaText,
} from '../contentGenerator/formulaUtils';
import {deriveSuperSku} from '../shared/reviewGrouping';

import {DEFAULT_LIMITS, TYPE_CONFIG} from './constants';

let reviewUid = 100;

export const uid = () => String(++reviewUid);

const formatVarLabel = (name = '') =>
    String(name)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

const PUBLISH_DESTINATION_LABELS = {
    shopify: 'Shopify',
    gmc: 'Google Merchant',
};

const BULLET_IGNORED_DEPENDENCY_VAR_NAMES = new Set(['product_name']);

const FIELD_PUBLISH_DESTINATIONS = {
    title: ['shopify', 'gmc'],
    description: ['shopify', 'gmc'],
    bullet: ['shopify'],
    faq: ['shopify'],
};

const dedupeIssues = (issues = []) => {
    const seen = new Set();

    return issues.filter((issue) => {
        if (!issue?.id) return false;
        if (seen.has(issue.id)) return false;
        seen.add(issue.id);
        return true;
    });
};

const parseSubgroupKeyParts = (value = '') => {
    const rawValue = String(value || '').trim();
    if (!rawValue) return {uniqueVarNames: [], valuesByName: {}};

    const valuesByName = {};
    const uniqueVarNames = rawValue
        .split('|')
        .map((segment) => segment.trim())
        .filter(Boolean)
        .map((segment) => {
            const separatorIndex = segment.indexOf(':');
            if (separatorIndex < 0) {
                valuesByName[segment] = '';
                return segment;
            }

            const name = segment.slice(0, separatorIndex).trim();
            const resolvedValue = segment.slice(separatorIndex + 1).trim();
            valuesByName[name] = resolvedValue;
            return name;
        })
        .filter(Boolean);

    return {uniqueVarNames, valuesByName};
};

export const normalizeFieldSubgroupMeta = (meta = null) => {
    if (!meta) return null;

    if (typeof meta === 'string') {
        const parsed = parseSubgroupKeyParts(meta);
        return normalizeFieldSubgroupMeta({
            uniqueVarNames: parsed.uniqueVarNames,
            groupKey: meta,
            valuesByName: parsed.valuesByName,
        });
    }

    const rawGroupKey = meta.groupKey || meta.group_key || '';
    const parsedGroupKey = parseSubgroupKeyParts(rawGroupKey);
    const uniqueVarNames = (
        Array.isArray(meta.uniqueVarNames)
            ? meta.uniqueVarNames
            : Array.isArray(meta.unique_var_names)
              ? meta.unique_var_names
              : parsedGroupKey.uniqueVarNames
    ).filter(Boolean);
    const valuesByName =
        meta.valuesByName && typeof meta.valuesByName === 'object'
            ? {...meta.valuesByName}
            : meta.values_by_name && typeof meta.values_by_name === 'object'
              ? {...meta.values_by_name}
              : parsedGroupKey.valuesByName;
    const groupKey =
        rawGroupKey ||
        uniqueVarNames.map((name) => `${name}:${valuesByName[name] || ''}`).join('|');

    if (!groupKey && !uniqueVarNames.length) return null;

    return {
        uniqueVarNames,
        groupKey,
        valuesByName,
    };
};

export const normalizeFieldDependencyMeta = (meta = null) => {
    if (!meta) return null;

    const contextVarNames = Array.isArray(meta.contextVarNames)
        ? meta.contextVarNames.filter(Boolean)
        : [];
    const templateValuesByName =
        meta.templateValuesByName && typeof meta.templateValuesByName === 'object'
            ? {...meta.templateValuesByName}
            : {};

    if (!contextVarNames.length) return null;

    return {
        source: meta.source || 'generated',
        contextVarNames,
        templateValuesByName,
        templateProductId: meta.templateProductId || '',
        templateSku: meta.templateSku || '',
    };
};

export const formatFieldSubgroupLabel = (meta = null) => {
    const subgroupMeta = normalizeFieldSubgroupMeta(meta);
    if (!subgroupMeta?.uniqueVarNames?.length) return '';

    return subgroupMeta.uniqueVarNames
        .map((name) => `${formatVarLabel(name)}: ${subgroupMeta.valuesByName?.[name] || 'Blank'}`)
        .join(' • ');
};

export const parseFieldSubgroupKey = (value = '') => {
    const rawValue = String(value || '').trim();
    if (!rawValue) return null;

    const {uniqueVarNames, valuesByName} = parseSubgroupKeyParts(rawValue);

    if (!uniqueVarNames.length) return null;

    return normalizeFieldSubgroupMeta({
        uniqueVarNames,
        groupKey: uniqueVarNames
            .map((name) => `${name}:${valuesByName[name] || ''}`)
            .join('|'),
        valuesByName,
    });
};

export const formatFieldSubgroupKey = (meta = null) =>
    normalizeFieldSubgroupMeta(meta)?.groupKey || '';

export const createField = (field = {}) => {
    const type = field.type || 'bullet';
    const label = field.label || TYPE_CONFIG[type]?.label || TYPE_CONFIG.bullet.label;
    const searchTerms = Array.isArray(field.searchTerms)
        ? field.searchTerms
        : typeof field.searchTerms === 'string'
          ? field.searchTerms
                .split('|')
                .map((term) => term.trim())
                .filter(Boolean)
          : [];

    return {
        id: field.id || `f${uid()}`,
        type,
        label,
        text: typeof field.text === 'string' ? field.text : '',
        // FAQ fields pair a question with an answer (stored in text).
        // Other field types ignore this property.
        question: type === 'faq' && typeof field.question === 'string'
            ? field.question
            : '',
        status: field.status || 'draft',
        history: Array.isArray(field.history) ? field.history.filter(Boolean) : [],
        searchTerms,
        subgroupMeta: normalizeFieldSubgroupMeta(field.subgroupMeta || field.subgroup_meta),
        dependencyMeta: normalizeFieldDependencyMeta(
            field.dependencyMeta || field.dependency_meta,
        ),
        // Issue IDs the user has explicitly ignored. Ignored issues
        // still render in the publish-check panel (muted, with a
        // Restore button), but don't block publish eligibility.
        ignoredIssueIds: Array.isArray(field.ignoredIssueIds)
            ? Array.from(
                  new Set(
                      field.ignoredIssueIds
                          .filter((id) => typeof id === 'string' && id.trim())
                          .map((id) => id.trim()),
                  ),
              )
            : [],
    };
};

export const normalizeListing = (listing = {}) => ({
    id: listing.id || `l${uid()}`,
    name: listing.name || 'Untitled listing',
    sku: listing.sku || '',
    category: listing.category || '',
    superSku: listing.superSku || listing.super_sku || '',
    sourceProductId: listing.sourceProductId || listing.source_product_id || '',
    generatorSignature: listing.generatorSignature || listing.generator_signature || '',
    editLinkKey:
        listing.editLinkKey ||
        listing.edit_link_key ||
        listing.generatorSignature ||
        listing.generator_signature ||
        '',
    fields: Array.isArray(listing.fields) ? listing.fields.map(createField) : [],
});

export const normalizeListings = (listings = []) => listings.map(normalizeListing);

export const getRelatedListingIds = (listings = [], listingId = '') => {
    const sourceListing = listings.find((listing) => listing.id === listingId);
    if (!sourceListing?.superSku) return [listingId];

    const relatedIds = listings
        .filter((listing) => listing.superSku === sourceListing.superSku)
        .map((listing) => listing.id);

    return relatedIds.length ? relatedIds : [listingId];
};

export const resolvePublishTargetListings = (
    listings = [],
    sourceListing = null,
    targetListingIds = null,
) => {
    if (Array.isArray(targetListingIds)) {
        const targetIds = new Set(targetListingIds);
        return listings.filter(
            (listing) => targetIds.has(listing.id) && listing.sourceProductId,
        );
    }

    if (sourceListing?.superSku) {
        return listings.filter(
            (listing) =>
                listing.superSku === sourceListing.superSku && listing.sourceProductId,
        );
    }

    return sourceListing?.sourceProductId ? [sourceListing] : [];
};

const getSameTypeFieldOrdinal = (listing, fieldId) => {
    const sourceField = listing?.fields?.find((field) => field.id === fieldId);
    if (!sourceField) return -1;

    return listing.fields
        .filter((field) => field.type === sourceField.type)
        .findIndex((field) => field.id === fieldId);
};

export const buildSameLevelFieldTargets = (
    listings = [],
    sourceListingId = '',
    sourceFieldId = '',
) => {
    const sourceListing = listings.find((listing) => listing.id === sourceListingId);
    const sourceField = sourceListing?.fields?.find((field) => field.id === sourceFieldId);
    const fieldOrdinal = getSameTypeFieldOrdinal(sourceListing, sourceFieldId);

    if (!sourceListing || !sourceField || fieldOrdinal < 0) {
        return {
            fieldOrdinal: -1,
            fieldType: '',
            missingListingIds: listings.map((listing) => listing.id),
            targets: [],
        };
    }

    const targets = [];
    const missingListingIds = [];

    listings.forEach((listing) => {
        const matchingField = listing.fields
            .filter((field) => field.type === sourceField.type)[fieldOrdinal];

        if (!matchingField) {
            missingListingIds.push(listing.id);
            return;
        }

        const subgroupKey =
            normalizeFieldSubgroupMeta(matchingField.subgroupMeta)?.groupKey || '';
        const logicalKey = listing.superSku
            ? [
                  'group',
                  listing.superSku,
                  listing.editLinkKey || listing.generatorSignature || '',
                  sourceField.type,
                  fieldOrdinal,
                  subgroupKey,
              ].join(':')
            : ['listing', listing.id, matchingField.id].join(':');

        targets.push({
            field: matchingField,
            fieldId: matchingField.id,
            listing,
            listingId: listing.id,
            logicalKey,
        });
    });

    return {
        fieldOrdinal,
        fieldType: sourceField.type,
        missingListingIds,
        targets,
    };
};

export const buildPerSkuCommandJobs = (targets = []) =>
    targets.map((target) => ({
        originalText: target.field.text,
        representative: target,
        targets: [target],
    }));

const COMMAND_CONTEXT_CORE_VARS = new Set([
    'product_name',
    'sku',
    'variant_title',
    'product_type',
    'vendor',
]);

const getCommandContextPriority = (name = '') => {
    if (COMMAND_CONTEXT_CORE_VARS.has(name)) return 0;
    if (/^component\d+_/.test(name)) return 1;
    if (/(finish|color|material|size|width|height|length|diameter|style|type)/.test(name)) {
        return 2;
    }
    return 3;
};

export const getCommandAttributeNames = (prompt = '') =>
    extractFormulaVarNames(prompt);

export const buildCommandInstruction = ({prompt = '', vars = []} = {}) => {
    const attributeNames = getCommandAttributeNames(prompt);
    if (!attributeNames.length) return prompt;

    const varsByName = new Map(vars.map((variable) => [variable.name, variable]));
    const attributeLines = attributeNames.map((name) => {
        const value = String(varsByName.get(name)?.sample ?? '').trim();
        return value
            ? `- {{${name}}}: ${value.slice(0, 240)}`
            : `- {{${name}}}: unavailable for this SKU`;
    });

    return [
        prompt,
        'SELECTED ATTRIBUTE CONTEXT (strict):',
        attributeLines.join('\n'),
        'Use only the selected attributes above as additional product context. Do not infer or use unlisted product fields, metafields, product names, SKUs, descriptions, or category details. The source text remains authoritative for facts it already contains. If a selected attribute is unavailable, do not guess its value. Keep any {{attribute_name}} token exactly as written if it appears in the rewritten output.',
    ].join('\n\n');
};

export const buildPerSkuCommandInstruction = ({
    prompt = '',
    listing = null,
    vars = [],
    maxAttributes = 60,
} = {}) => {
    const selectedAttributeNames = getCommandAttributeNames(prompt);
    if (selectedAttributeNames.length) {
        return buildCommandInstruction({prompt, vars});
    }

    const contextVars = vars
        .map((variable, index) => ({...variable, index}))
        .filter(
            (variable) =>
                variable.name &&
                variable.name !== 'description' &&
                String(variable.sample ?? '').trim(),
        )
        .sort((left, right) => {
            const priorityDifference =
                getCommandContextPriority(left.name) -
                getCommandContextPriority(right.name);
            return priorityDifference || left.index - right.index;
        })
        .slice(0, maxAttributes);
    const attributeLines = contextVars.map((variable) => {
        const value = String(variable.sample).trim().slice(0, 240);
        return `- ${variable.name.replace(/_/g, ' ')}: ${value}`;
    });

    return [
        prompt,
        'Apply this command specifically to the selected SKU below. Ground the rewrite in this SKU\'s attributes, not another SKU or a generic group-level product. Remove or correct claims that conflict with these attributes. Do not invent missing specifications.',
        `Product: ${listing?.name || 'Unknown'}`,
        `SKU: ${listing?.sku || 'Unknown'}`,
        listing?.category ? `Category: ${listing.category}` : '',
        attributeLines.length
            ? `SKU-specific attributes:\n${attributeLines.join('\n')}`
            : '',
    ]
        .filter(Boolean)
        .join('\n\n');
};

export const applyBatchFieldTextUpdates = (listings = [], updates = []) => {
    const updatesByTarget = new Map(
        updates
            .filter(
                (update) =>
                    update?.listingId &&
                    update?.fieldId &&
                    typeof update?.text === 'string' &&
                    update.text.trim(),
            )
            .map((update) => [`${update.listingId}:${update.fieldId}`, update]),
    );

    if (!updatesByTarget.size) return listings;

    return listings.map((listing) => {
        let changed = false;
        const fields = listing.fields.map((field) => {
            const update = updatesByTarget.get(`${listing.id}:${field.id}`);
            if (!update || update.text === field.text) return field;
            if (
                typeof update.expectedText === 'string' &&
                field.text !== update.expectedText
            ) {
                return field;
            }

            changed = true;
            return {
                ...field,
                text: update.text,
                status: 'revised',
                history: [
                    ...(Array.isArray(field.history) ? field.history : []),
                    field.text,
                ].filter(Boolean),
            };
        });

        return changed ? {...listing, fields} : listing;
    });
};

export const getGeneratedResultSignature = (generatedResult) =>
    JSON.stringify({
        title: generatedResult?.title || '',
        desc: generatedResult?.desc || '',
        bullets: Array.isArray(generatedResult?.bullets)
            ? generatedResult.bullets.map((bullet) => ({
                  field: bullet?.field || '',
                  text: bullet?.text || '',
              }))
            : [],
        faqs: Array.isArray(generatedResult?.faqs)
            ? generatedResult.faqs.map((faq) => ({
                  question: faq?.question || '',
                  answer: faq?.answer || '',
              }))
            : [],
        fieldSubgroups: {
            title: normalizeFieldSubgroupMeta(generatedResult?.fieldSubgroups?.title),
            description: normalizeFieldSubgroupMeta(
                generatedResult?.fieldSubgroups?.description,
            ),
            bullets: Array.isArray(generatedResult?.fieldSubgroups?.bullets)
                ? generatedResult.fieldSubgroups.bullets.map((meta) =>
                      normalizeFieldSubgroupMeta(meta),
                  )
                : [],
            faqs: Array.isArray(generatedResult?.fieldSubgroups?.faqs)
                ? generatedResult.fieldSubgroups.faqs.map((meta) =>
                      normalizeFieldSubgroupMeta(meta),
                  )
                : [],
        },
        fieldDependencies: {
            title: normalizeFieldDependencyMeta(generatedResult?.fieldDependencies?.title),
            description: normalizeFieldDependencyMeta(
                generatedResult?.fieldDependencies?.description,
            ),
            bullets: Array.isArray(generatedResult?.fieldDependencies?.bullets)
                ? generatedResult.fieldDependencies.bullets.map((meta) =>
                      normalizeFieldDependencyMeta(meta),
                  )
                : [],
            faqs: Array.isArray(generatedResult?.fieldDependencies?.faqs)
                ? generatedResult.fieldDependencies.faqs.map((meta) =>
                      normalizeFieldDependencyMeta(meta),
                  )
                : [],
        },
    });

const productToListing = (product, superSku, generatedResult, generatorSignature = '', shopifyMapping = null) => {
    if (generatedResult) {
        return normalizeListing({
            id: `p-${product.id}`,
            name: product.name || product.sku,
            sku: product.sku || product.id,
            category: product.category || '',
            superSku,
            sourceProductId: product.id,
            generatorSignature,
            fields: [
                ...(generatedResult.title
                    ? [
                          {
                              type: 'title',
                              label: 'Title',
                              text: generatedResult.title,
                              subgroupMeta: generatedResult?.fieldSubgroups?.title || null,
                              dependencyMeta:
                                  generatedResult?.fieldDependencies?.title || null,
                          },
                      ]
                    : []),
                {
                    type: 'description',
                    label: 'Description',
                    text: generatedResult.desc || '',
                    subgroupMeta: generatedResult?.fieldSubgroups?.description || null,
                    dependencyMeta: generatedResult?.fieldDependencies?.description || null,
                },
                ...(generatedResult.bullets || []).map((bullet, index) => ({
                    type: 'bullet',
                    label: bullet.field,
                    text: bullet.text,
                    subgroupMeta: generatedResult?.fieldSubgroups?.bullets?.[index] || null,
                    dependencyMeta:
                        generatedResult?.fieldDependencies?.bullets?.[index] || null,
                })),
                ...(generatedResult.faqs || []).map((faq, index) => ({
                    type: 'faq',
                    label: `FAQ ${index + 1}`,
                    question: faq.question || '',
                    text: faq.answer || '',
                    subgroupMeta: generatedResult?.fieldSubgroups?.faqs?.[index] || null,
                    dependencyMeta:
                        generatedResult?.fieldDependencies?.faqs?.[index] || null,
                })),
            ],
        });
    }

    // Pre-populate from Shopify product data using mapping
    const raw = product.raw || {};
    const metafields = raw.metafields || {};
    const mapping = shopifyMapping || {};

    // Read title
    const titleMapping = mapping.title || {type: 'native'};
    let shopifyTitle;
    if (titleMapping.type === 'metafield' && titleMapping.source) {
        shopifyTitle = metafields[titleMapping.source] || raw.title || product.name || '';
    } else {
        shopifyTitle = raw.title || product.name || '';
    }

    // Read description
    const descMapping = mapping.description || {type: 'native'};
    let description;
    if (descMapping.type === 'metafield' && descMapping.source) {
        description = metafields[descMapping.source] || '';
    } else {
        const bodyHtml = raw.body_html || '';
        const descriptionHtml = bodyHtml
            .replace(/<ul[\s\S]*?<\/ul>/gi, '')
            .replace(/<ol[\s\S]*?<\/ol>/gi, '');
        description = descriptionHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }

    // Read bullets
    const bulletMapping = mapping.bullets || {type: 'html_list'};
    let bullets = [];
    if (bulletMapping.type === 'metafield' && bulletMapping.source) {
        const raw_bullets = metafields[bulletMapping.source];
        try {
            const parsed = typeof raw_bullets === 'string' ? JSON.parse(raw_bullets) : raw_bullets;
            if (Array.isArray(parsed)) {
                bullets = parsed.map((text, i) => ({type: 'bullet', label: `Bullet ${i + 1}`, text: String(text)}));
            }
        } catch {
            if (raw_bullets) bullets = [{type: 'bullet', label: 'Bullet 1', text: String(raw_bullets)}];
        }
    } else {
        const bodyHtml = raw.body_html || '';
        const bulletMatches = bodyHtml.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [];
        bullets = bulletMatches.map((li, i) => ({
            type: 'bullet',
            label: `Bullet ${i + 1}`,
            text: li.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(),
        })).filter((b) => b.text);
    }

    // Rehydrate FAQs from the saved metafield. The namespace comes back
    // from Shopify in resolved form for app-owned metafields (e.g.
    // `app--{client-id}--contentlab`), so match by key suffix instead of
    // an exact namespace lookup. Saved FAQs come in as `approved` since
    // they were approved when published — re-approval shouldn't be
    // required just to reopen the listing.
    const faqsRaw = readContentlabFaqMetafield(metafields, 'faqs');
    const faqs = parseSavedFaqs(faqsRaw);

    return normalizeListing({
        id: `p-${product.id}`,
        name: product.name || product.sku,
        sku: product.sku || product.id,
        category: product.category || '',
        superSku,
        sourceProductId: product.id,
        fields: [
            {type: 'title', label: 'Title', text: shopifyTitle},
            {type: 'description', label: 'Description', text: description},
            ...bullets,
            ...faqs,
        ],
    });
};

const readContentlabFaqMetafield = (metafields = {}, key = '') => {
    if (!metafields || typeof metafields !== 'object' || !key) return null;
    const suffix = `.${key}`;
    const matchKey = Object.keys(metafields).find((mfKey) => {
        if (!mfKey.endsWith(suffix)) return false;
        const namespace = mfKey.slice(0, -suffix.length);
        // Accept either the plain namespace (legacy) or the resolved
        // app-owned form like `app--{client-id}--contentlab`.
        return (
            namespace === 'contentlab' ||
            /^app--.*--contentlab$/.test(namespace)
        );
    });
    return matchKey ? metafields[matchKey] : null;
};

const parseSavedFaqs = (rawValue) => {
    if (!rawValue) return [];
    let parsed;
    try {
        parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
    } catch {
        return [];
    }
    if (!Array.isArray(parsed)) return [];
    return parsed
        .filter((faq) => faq?.question && faq?.answer)
        .map((faq, index) => ({
            type: 'faq',
            label: `FAQ ${index + 1}`,
            question: String(faq.question),
            text: String(faq.answer),
            status: 'approved',
        }));
};

export const resetListingFromProduct = (listing, product, shopifyMapping = null) => {
    if (!product) return listing;
    const fresh = productToListing(product, listing.superSku || '', null, '', shopifyMapping);
    return {...fresh, id: listing.id};
};

export const resetListingsFromProducts = (
    listings = [],
    products = [],
    shopifyMapping = null,
) => {
    const productsByKey = new Map();
    products.forEach((product) => {
        [product?.id, product?.shopifyId, product?.sku]
            .filter((value) => value !== undefined && value !== null && String(value).trim())
            .forEach((value) => {
                productsByKey.set(String(value).trim().toLowerCase(), product);
            });
    });

    const missingListingIds = [];
    let refreshedCount = 0;
    const nextListings = listings.map((listing) => {
        if (!listing.sourceProductId) return listing;

        const lookupKeys = [listing.sourceProductId, listing.sku]
            .filter((value) => value !== undefined && value !== null && String(value).trim())
            .map((value) => String(value).trim().toLowerCase());
        const product = lookupKeys
            .map((key) => productsByKey.get(key))
            .find(Boolean);

        if (!product) {
            missingListingIds.push(listing.id);
            return listing;
        }

        refreshedCount += 1;
        return resetListingFromProduct(listing, product, shopifyMapping);
    });

    return {
        listings: nextListings,
        missingListingIds,
        refreshedCount,
    };
};

export const buildReviewListings = (
    selectedProducts = [],
    resultsByProductId = {},
    currentListings = [],
    reviewGroupByProductId = {},
    shopifyMapping = null,
) => {
    if (!selectedProducts.length) return [];

    const existingById = new Map(currentListings.map((listing) => [listing.id, listing]));
    const derivedGroupCounts = selectedProducts.reduce((counts, product) => {
        const derivedGroup = deriveSuperSku(product.sku);
        if (!derivedGroup) return counts;

        counts.set(derivedGroup, (counts.get(derivedGroup) || 0) + 1);
        return counts;
    }, new Map());
    const productEntries = selectedProducts.map((product) => {
        const listingId = `p-${product.id}`;
        const existing = existingById.get(listingId);
        const derivedGroup = deriveSuperSku(product.sku);

        return {
            product,
            listingId,
            existing,
            superSku: Object.prototype.hasOwnProperty.call(reviewGroupByProductId, product.id)
                ? reviewGroupByProductId[product.id] || ''
                : existing?.superSku ||
                  (derivedGroup && derivedGroupCounts.get(derivedGroup) > 1
                      ? derivedGroup
                      : ''),
        };
    });
    const groupTemplates = new Map();

    productEntries.forEach((entry) => {
        if (!entry.superSku) return;

        if (!groupTemplates.has(entry.superSku)) {
            groupTemplates.set(entry.superSku, []);
        }

        groupTemplates.get(entry.superSku).push(entry);
    });

    return productEntries.map(({existing, product, superSku}) => {
        const groupEntries = superSku ? groupTemplates.get(superSku) || [] : [];
        const isSharedGroup = groupEntries.length > 1;
        const generatedEntries = groupEntries.filter(
            ({product: groupProduct}) => resultsByProductId[groupProduct.id],
        );
        const generatedSignatures = Array.from(
            new Set(
                generatedEntries.map(({product: groupProduct}) =>
                    getGeneratedResultSignature(resultsByProductId[groupProduct.id]),
                ),
            ),
        );
        const canShareGeneratedResult =
            isSharedGroup && generatedEntries.length > 0 && generatedSignatures.length === 1;
        const templateEntry = canShareGeneratedResult
            ? generatedEntries[0] || groupEntries[0]
            : null;
        const generatedResult = canShareGeneratedResult
            ? resultsByProductId[templateEntry?.product.id] || null
            : resultsByProductId[product.id] || null;
        const generatorSignature = generatedResult
            ? `${canShareGeneratedResult ? `${superSku}::` : ''}${getGeneratedResultSignature(generatedResult)}`
            : '';
        const productName = product.name || product.sku;
        const productSku = product.sku || product.id;
        const productCategory = product.category || '';
        const matchesProductIdentity =
            existing?.name === productName &&
            existing?.sku === productSku &&
            existing?.category === productCategory;
        const matchesProductContext =
            matchesProductIdentity && existing?.superSku === superSku;

        if (generatedResult) {
            if (
                existing &&
                existing.generatorSignature === generatorSignature &&
                matchesProductContext
            ) {
                return existing;
            }

            return productToListing(
                product,
                superSku,
                generatedResult,
                generatorSignature,
                shopifyMapping,
            );
        }

        if (existing && matchesProductContext) {
            return existing;
        }

        // Product identity is the same but the group association
        // changed (typical when loading a saved review config: state
        // updates can settle in an order that briefly leaves the
        // existing listing's superSku stale). Preserve the existing
        // fields (which carry the loaded config's data, including FAQ
        // questions) and just refresh the superSku — falling through to
        // productToListing here would replace user-edited content with
        // whatever's in the Shopify metafields.
        if (existing && matchesProductIdentity) {
            return {...existing, superSku};
        }

        const rebuilt = productToListing(product, superSku, null, '', shopifyMapping);

        // Last-resort defense: even when product identity didn't match,
        // if the existing listing has FAQ fields with user-entered
        // content (question OR answer), prefer them over the
        // metafield-derived FAQ fields. A saved review config's FAQs are
        // the source of truth — we shouldn't silently revert them to
        // whatever Shopify last received.
        if (existing) {
            const existingFaqs = existing.fields.filter(
                (field) =>
                    field.type === 'faq' &&
                    (((field.question || '').trim()) || ((field.text || '').trim())),
            );
            if (existingFaqs.length) {
                const nonFaqFields = rebuilt.fields.filter(
                    (field) => field.type !== 'faq',
                );
                return {
                    ...rebuilt,
                    fields: [...nonFaqFields, ...existingFaqs],
                };
            }
        }

        return rebuilt;
    });
};

export const mergeListingVars = (baseVars = [], productVars = []) => {
    if (!productVars.length) return baseVars;

    const productVarsByName = new Map(productVars.map((variable) => [variable.name, variable]));
    const merged = baseVars.map((variable) => {
        const productVariable = productVarsByName.get(variable.name);

        if (!productVariable) return variable;

        productVarsByName.delete(variable.name);
        return {...variable, sample: productVariable.sample};
    });

    productVarsByName.forEach((variable) => {
        merged.push(variable);
    });

    return merged;
};

export const getFieldByType = (listing, type) =>
    listing?.fields?.find((field) => field.type === type) || null;

export const getDescriptionField = (listing) => getFieldByType(listing, 'description');
export const getTitleField = (listing) => getFieldByType(listing, 'title');
export const getBulletFields = (listing) =>
    listing?.fields?.filter((field) => field.type === 'bullet') || [];
export const getFaqFields = (listing) =>
    listing?.fields?.filter((field) => field.type === 'faq') || [];

export const resolveListingText = (text, vars = []) => {
    if (typeof text !== 'string') return '';
    return text.includes('{{') ? resolveVars(text, vars) : text;
};

const getFieldPublishDestinations = (field = {}) =>
    FIELD_PUBLISH_DESTINATIONS[field.type] || ['shopify', 'gmc'];

const buildFieldIssue = ({
    id,
    kind,
    field,
    message,
    reference = '',
}) => ({
    id,
    kind,
    fieldId: field?.id || '',
    destinations: getFieldPublishDestinations(field),
    message,
    reference,
});

const collectLiveFormulaPublishIssues = (field, vars = []) => {
    const text = typeof field?.text === 'string' ? field.text : '';
    if (!text.includes('{{')) return [];

    const issues = [];

    // Issue IDs intentionally exclude field.id so that the SAME issue
    // (same kind + reference) across linked listings in a group
    // produces an identical ID. The review screen's updateField()
    // already mirrors patches to every matching field in linked
    // listings; with stable IDs across the group, "Ignore" on one
    // variant now propagates to every variant automatically.
    //
    // Iterate per-token so pipe-fallback chains ({{finish|custom_finish}})
    // only get flagged when the WHOLE chain has no resolvable value.
    tokenizeFormulaText(text).forEach((token) => {
        if (token.type !== 'formula') return;

        const expression = token.value.slice(2, -2).trim();
        const resolvedToken = resolveFormula(expression, vars);
        const tokenResolved =
            typeof resolvedToken === 'string' &&
            !(resolvedToken.startsWith('{{') && resolvedToken.endsWith('}}'));
        if (tokenResolved) return;

        const tokenReferences = extractFormulaVarNames(token.value);
        const allReferencesMissing = tokenReferences.every(
            (reference) => !resolveVariableReferenceValue(reference, vars).found,
        );

        if (allReferencesMissing && tokenReferences.length) {
            const primaryRef = tokenReferences[0];
            issues.push(
                buildFieldIssue({
                    id: `live-missing:${primaryRef}`,
                    kind: 'missing_reference',
                    field,
                    reference: primaryRef,
                    message: `Missing \`${primaryRef}\` for this SKU.`,
                }),
            );
            return;
        }

        issues.push(
            buildFieldIssue({
                id: `live-unresolved:${expression}`,
                kind: 'unresolved_formula',
                field,
                reference: token.value,
                message: `Could not resolve \`${token.value}\` for this SKU.`,
            }),
        );
    });

    return dedupeIssues(issues);
};

const collectDependencyPublishIssues = (field, vars = []) => {
    const dependencyMeta = normalizeFieldDependencyMeta(field?.dependencyMeta);
    if (!dependencyMeta?.contextVarNames?.length) return [];

    const subgroupMeta = normalizeFieldSubgroupMeta(field?.subgroupMeta);
    const safeSubgroupVarNames = new Set(subgroupMeta?.uniqueVarNames || []);
    const fieldText = typeof field?.text === 'string' ? field.text : '';
    const liveMappedReferences = new Set(extractFormulaVarNames(fieldText));

    // A context var is considered "covered by the live text" when it
    // appears inside a {{...}} token whose chain successfully resolves
    // for this SKU. That includes pipe-fallback chains
    // ({{ola|custom_material}}) where the primary var is missing but a
    // fallback segment fills in — the field text still resolves cleanly,
    // so the missing primary shouldn't surface a publish issue.
    const coveredByResolvingTokens = new Set();
    tokenizeFormulaText(fieldText).forEach((token) => {
        if (token.type !== 'formula') return;
        const expression = token.value.slice(2, -2).trim();
        const resolvedToken = resolveFormula(expression, vars);
        const looksUnresolved =
            typeof resolvedToken === 'string' &&
            resolvedToken.startsWith('{{') &&
            resolvedToken.endsWith('}}');
        if (looksUnresolved) return;
        extractFormulaVarNames(token.value).forEach((name) =>
            coveredByResolvingTokens.add(name),
        );
    });

    const issues = [];

    dependencyMeta.contextVarNames.forEach((reference) => {
        if (
            field?.type === 'bullet' &&
            BULLET_IGNORED_DEPENDENCY_VAR_NAMES.has(reference)
        ) {
            return;
        }

        const resolvedReference = resolveVariableReferenceValue(reference, vars);
        const templateValue = dependencyMeta.templateValuesByName?.[reference] || '';

        if (!resolvedReference.found) {
            if (coveredByResolvingTokens.has(reference)) return;
            issues.push(
                buildFieldIssue({
                    // Field.id intentionally omitted — same kind +
                    // reference resolves to the same ID across linked
                    // listings so Ignore propagates group-wide.
                    id: `dependency-missing:${reference}`,
                    kind: 'generated_missing_reference',
                    field,
                    reference,
                    message: `Generated from \`${reference}\`, but this SKU has no value for it.`,
                }),
            );
            return;
        }

        if (safeSubgroupVarNames.has(reference)) return;
        if (liveMappedReferences.has(reference)) return;
        if (coveredByResolvingTokens.has(reference)) return;
        if (templateValue === resolvedReference.value) return;

        issues.push(
            buildFieldIssue({
                id: `dependency-unmapped:${reference}`,
                kind: 'generated_missing_dynamic_mapping',
                field,
                reference,
                message: `Generated using \`${formatVarLabel(reference)}\` with template value \`${templateValue || 'Blank'}\`, but this SKU resolves to \`${resolvedReference.value || 'Blank'}\` and the field has no live \`{{${reference}}}\` mapping or subgroup split.`,
            }),
        );
    });

    return dedupeIssues(issues);
};

const buildFieldPublishValidation = (field, vars = []) => {
    const applicableDestinations = getFieldPublishDestinations(field);
    const ignoredIssueIds = new Set(
        Array.isArray(field?.ignoredIssueIds) ? field.ignoredIssueIds : [],
    );
    // Annotate every issue with whether the user has ignored it. The UI
    // still renders ignored issues (so the user can un-ignore), but
    // they're skipped from publish-safety checks below.
    const issues = dedupeIssues([
        ...collectLiveFormulaPublishIssues(field, vars),
        ...collectDependencyPublishIssues(field, vars),
    ]).map((issue) => ({
        ...issue,
        isIgnored: ignoredIssueIds.has(issue.id),
    }));
    const activeIssues = issues.filter((issue) => !issue.isIgnored);
    const issuesByDestination = Object.fromEntries(
        Object.keys(PUBLISH_DESTINATION_LABELS).map((destination) => [
            destination,
            applicableDestinations.includes(destination)
                ? activeIssues.filter((issue) => issue.destinations.includes(destination))
                : [],
        ]),
    );
    const isApproved = field?.status === 'approved';
    const isPublishSafeByDestination = Object.fromEntries(
        Object.keys(PUBLISH_DESTINATION_LABELS).map((destination) => [
            destination,
            applicableDestinations.includes(destination) &&
            issuesByDestination[destination].length === 0,
        ]),
    );

    return {
        field,
        issues,
        // Non-ignored issues only — used by the workflow status
        // resolver so ignored issues don't keep a field flagged.
        activeIssues,
        applicableDestinations,
        issuesByDestination,
        isApproved,
        isPublishSafeByDestination,
        isEligibleByDestination: Object.fromEntries(
            Object.keys(PUBLISH_DESTINATION_LABELS).map((destination) => [
                destination,
                isApproved && isPublishSafeByDestination[destination],
            ]),
        ),
    };
};

const buildDestinationPublishState = (destination, fieldStates = []) => {
    const applicableFieldStates = fieldStates.filter((fieldState) =>
        fieldState.applicableDestinations.includes(destination),
    );
    const approvedFieldStates = applicableFieldStates.filter((fieldState) => fieldState.isApproved);
    const blockedApprovedFieldStates = approvedFieldStates.filter(
        (fieldState) => fieldState.issuesByDestination[destination].length > 0,
    );
    const eligibleTitleField = approvedFieldStates.find(
        (fieldState) =>
            fieldState.field.type === 'title' &&
            fieldState.isEligibleByDestination[destination],
    );
    const eligibleDescriptionField = approvedFieldStates.find(
        (fieldState) =>
            fieldState.field.type === 'description' &&
            fieldState.isEligibleByDestination[destination],
    );
    const nonEmptyBulletStates = applicableFieldStates.filter(
        (fieldState) =>
            fieldState.field.type === 'bullet' && fieldState.field.text.trim(),
    );
    const eligibleBulletStates = nonEmptyBulletStates.filter(
        (fieldState) => fieldState.isEligibleByDestination[destination],
    );
    const allNonEmptyBulletsEligible =
        destination === 'shopify' &&
        nonEmptyBulletStates.length > 0 &&
        eligibleBulletStates.length === nonEmptyBulletStates.length;

    // FAQs follow the same all-or-nothing rule as bullets: the FAQPage
    // JSON-LD schema gets published as a single set, so pushing a partial
    // list would broadcast an incomplete FAQ list with the schema marker.
    const nonEmptyFaqStates = applicableFieldStates.filter(
        (fieldState) =>
            fieldState.field.type === 'faq' &&
            (fieldState.field.question || '').trim() &&
            (fieldState.field.text || '').trim(),
    );
    const eligibleFaqStates = nonEmptyFaqStates.filter(
        (fieldState) => fieldState.isEligibleByDestination[destination],
    );
    const allNonEmptyFaqsEligible =
        destination === 'shopify' &&
        nonEmptyFaqStates.length > 0 &&
        eligibleFaqStates.length === nonEmptyFaqStates.length;

    const eligibleFieldIds = [
        ...(eligibleTitleField ? [eligibleTitleField.field.id] : []),
        ...(eligibleDescriptionField ? [eligibleDescriptionField.field.id] : []),
        ...(allNonEmptyBulletsEligible
            ? eligibleBulletStates.map((fieldState) => fieldState.field.id)
            : []),
        ...(allNonEmptyFaqsEligible
            ? eligibleFaqStates.map((fieldState) => fieldState.field.id)
            : []),
    ];
    const issues = dedupeIssues(
        blockedApprovedFieldStates.flatMap((fieldState) =>
            fieldState.issuesByDestination[destination].map((issue) => ({
                ...issue,
                fieldId: fieldState.field.id,
                fieldLabel: fieldState.field.label,
                fieldType: fieldState.field.type,
            })),
        ),
    );

    return {
        destination,
        label: PUBLISH_DESTINATION_LABELS[destination],
        issues,
        applicableFieldIds: applicableFieldStates.map((fieldState) => fieldState.field.id),
        blockedApprovedFieldIds: blockedApprovedFieldStates.map(
            (fieldState) => fieldState.field.id,
        ),
        eligibleFieldIds,
        eligibleBulletFieldIds: allNonEmptyBulletsEligible
            ? eligibleBulletStates.map((fieldState) => fieldState.field.id)
            : [],
        hasEligibleContent: eligibleFieldIds.length > 0,
        summary: blockedApprovedFieldStates.length
            ? `${blockedApprovedFieldStates.length} approved field(s) blocked for ${PUBLISH_DESTINATION_LABELS[destination]}.`
            : '',
    };
};

export const validateListingPublishSafety = (listing, vars = []) => {
    const fieldStates = (listing?.fields || []).map((field) =>
        buildFieldPublishValidation(field, vars),
    );
    const fieldsById = Object.fromEntries(
        fieldStates.map((fieldState) => [fieldState.field.id, fieldState]),
    );
    const destinations = Object.fromEntries(
        Object.keys(PUBLISH_DESTINATION_LABELS).map((destination) => [
            destination,
            buildDestinationPublishState(destination, fieldStates),
        ]),
    );

    return {
        fields: fieldStates,
        fieldsById,
        destinations,
        hasIssues: fieldStates.some((fieldState) => fieldState.issues.length > 0),
        hasApprovedBlockingIssues: Object.values(destinations).some(
            (destinationState) => destinationState.blockedApprovedFieldIds.length > 0,
        ),
    };
};

export const getFieldWorkflowStatus = (field = {}, fieldValidation = null) => {
    // Only non-ignored issues drive the "flagged" status — once the user
    // explicitly ignores an issue, the field shouldn't keep wearing the
    // flag chip.
    const activeIssues =
        fieldValidation?.activeIssues ?? fieldValidation?.issues ?? [];
    if (activeIssues.length) return 'flagged';
    return field?.status || 'draft';
};

export const applyWorkflowStatusesToListing = (listing, publishValidation = null) => ({
    ...listing,
    fields: (listing?.fields || []).map((field) => ({
        ...field,
        status: getFieldWorkflowStatus(
            field,
            publishValidation?.fieldsById?.[field.id] || null,
        ),
    })),
});

export const getListingStats = (listing, publishValidation = null) => {
    const fields = listing?.fields || [];
    const approved = fields.filter(
        (field) =>
            getFieldWorkflowStatus(
                field,
                publishValidation?.fieldsById?.[field.id] || null,
            ) === 'approved',
    ).length;
    const flagged = fields.filter(
        (field) =>
            getFieldWorkflowStatus(
                field,
                publishValidation?.fieldsById?.[field.id] || null,
            ) === 'flagged',
    ).length;

    return {
        total: fields.length,
        approved,
        flagged,
        pending: Math.max(fields.length - approved - flagged, 0),
    };
};

export const collectListingSearchTerms = (listing) =>
    Array.from(
        new Set(
            (listing?.fields || [])
                .flatMap((field) => field.searchTerms || [])
                .map((term) => term.trim())
                .filter(Boolean),
        ),
    );

export const buildDescriptionContext = (
    listing,
    charLimit = DEFAULT_LIMITS.description,
    vars = [],
) => ({
    name: listing?.name || '',
    sku: listing?.sku || '',
    category: listing?.category || '',
    superSku: listing?.superSku || '',
    title: resolveListingText(getTitleField(listing)?.text || '', vars),
    description: resolveListingText(getDescriptionField(listing)?.text || '', vars),
    bullets: getBulletFields(listing).map((field) => ({
        label: field.label,
        text: resolveListingText(field.text, vars),
    })),
    searchTerms: collectListingSearchTerms(listing),
    charLimit,
});

export const filterListings = (
    listings,
    search,
    filterMode,
    publishValidationByListingId = {},
) =>
    listings.filter((listing) => {
        const query = search.trim().toLowerCase();
        const matchesSearch =
            !query ||
            listing.name.toLowerCase().includes(query) ||
            listing.sku.toLowerCase().includes(query) ||
            listing.superSku.toLowerCase().includes(query);

        if (!matchesSearch) return false;
        const publishValidation = publishValidationByListingId?.[listing.id] || null;
        const workflowStatuses = listing.fields.map((field) =>
            getFieldWorkflowStatus(
                field,
                publishValidation?.fieldsById?.[field.id] || null,
            ),
        );

        if (filterMode === 'pending') {
            return workflowStatuses.some(
                (status) => status === 'draft' || status === 'revised',
            );
        }
        if (filterMode === 'approved') {
            return (
                listing.fields.length > 0 &&
                workflowStatuses.every((status) => status === 'approved')
            );
        }
        if (filterMode === 'flagged') {
            return workflowStatuses.some((status) => status === 'flagged');
        }
        return true;
    });

export const buildSidebarGroups = (listings) => {
    const groups = [];
    const seenGroups = new Set();

    listings.forEach((listing) => {
        if (listing.superSku) {
            if (seenGroups.has(listing.superSku)) return;
            seenGroups.add(listing.superSku);
            groups.push({
                type: 'group',
                key: listing.superSku,
                listings: listings.filter((candidate) => candidate.superSku === listing.superSku),
            });
            return;
        }

        groups.push({type: 'single', listing});
    });

    return groups;
};

const escapeCsvValue = (value = '') => {
    const stringValue = String(value ?? '');
    if (!/[",\n]/.test(stringValue)) return stringValue;

    return `"${stringValue.replace(/"/g, '""')}"`;
};

const parseCsvLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    values.push(current.trim());
    return values;
};

export const parseReviewCsv = (text) => {
    const lines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    if (lines.length < 2) return [];

    const headers = parseCsvLine(lines[0]).map((header) => header.toLowerCase());

    return lines.slice(1).map((line) => {
        const values = parseCsvLine(line);
        const row = Object.fromEntries(headers.map((header, index) => [header, values[index] || '']));
        const fields = [];

        if (row.title || row.title_subgroup || row.title_group) {
            fields.push(
                createField({
                    type: 'title',
                    label: 'Title',
                    text: row.title || '',
                    subgroupMeta: parseFieldSubgroupKey(
                        row.title_subgroup || row.title_group || '',
                    ),
                }),
            );
        }
        if (row.description || row.description_subgroup || row.description_group) {
            fields.push(
                createField({
                    type: 'description',
                    label: 'Description',
                    text: row.description || '',
                    subgroupMeta: parseFieldSubgroupKey(
                        row.description_subgroup || row.description_group || '',
                    ),
                }),
            );
        }

        const bulletIndexes = Array.from(
            new Set(
                Object.keys(row)
                    .map((key) => {
                        const match = key.match(/^bullet_(\d+)(?:_(label|subgroup|group))?$/);
                        return match ? match[1] : null;
                    })
                    .filter(Boolean),
            ),
        ).sort((left, right) => Number(left) - Number(right));

        bulletIndexes.forEach((bulletIndex) => {
            const bulletText = row[`bullet_${bulletIndex}`] || '';
            const rawBulletLabel = row[`bullet_${bulletIndex}_label`] || '';
            const subgroupValue =
                row[`bullet_${bulletIndex}_subgroup`] ||
                row[`bullet_${bulletIndex}_group`] ||
                '';
            const bulletLabel = rawBulletLabel || `Bullet ${bulletIndex}`;

            if (!bulletText && !rawBulletLabel && !subgroupValue) return;

            fields.push(
                createField({
                    type: 'bullet',
                    label: bulletLabel,
                    text: bulletText,
                    subgroupMeta: parseFieldSubgroupKey(subgroupValue),
                }),
            );
        });

        // FAQ columns: faq_<n>_question, faq_<n>_answer (+ optional
        // _subgroup). Each row that has either a question or answer
        // becomes a FAQ field.
        const faqIndexes = Array.from(
            new Set(
                Object.keys(row)
                    .map((key) => {
                        const match = key.match(
                            /^faq_(\d+)(?:_(question|answer|subgroup|group))?$/,
                        );
                        return match ? match[1] : null;
                    })
                    .filter(Boolean),
            ),
        ).sort((left, right) => Number(left) - Number(right));

        faqIndexes.forEach((faqIndex) => {
            const question = row[`faq_${faqIndex}_question`] || '';
            const answer = row[`faq_${faqIndex}_answer`] || '';
            const subgroupValue =
                row[`faq_${faqIndex}_subgroup`] ||
                row[`faq_${faqIndex}_group`] ||
                '';

            if (!question.trim() && !answer.trim() && !subgroupValue) return;

            fields.push(
                createField({
                    type: 'faq',
                    label: `FAQ ${faqIndex}`,
                    question,
                    text: answer,
                    subgroupMeta: parseFieldSubgroupKey(subgroupValue),
                }),
            );
        });

        return normalizeListing({
            name: row.name || 'Untitled listing',
            sku: row.sku || '',
            category: row.category || '',
            superSku: row.group || row.super_sku || '',
            fields,
        });
    });
};

export const formatReviewCsv = (listings = []) => {
    if (!listings.length) return '';

    const maxBulletCount = listings.reduce((count, listing) => {
        const bulletCount = (listing.fields || []).filter((field) => field.type === 'bullet').length;
        return Math.max(count, bulletCount);
    }, 0);
    const maxFaqCount = listings.reduce((count, listing) => {
        const faqCount = (listing.fields || []).filter((field) => field.type === 'faq').length;
        return Math.max(count, faqCount);
    }, 0);
    const headers = [
        'name',
        'sku',
        'group',
        'category',
        'title',
        'title_subgroup',
        'description',
        'description_subgroup',
        ...Array.from({length: maxBulletCount}, (_item, index) => [
            `bullet_${index + 1}_label`,
            `bullet_${index + 1}`,
            `bullet_${index + 1}_subgroup`,
        ]).flat(),
        ...Array.from({length: maxFaqCount}, (_item, index) => [
            `faq_${index + 1}_question`,
            `faq_${index + 1}_answer`,
            `faq_${index + 1}_subgroup`,
        ]).flat(),
    ];

    const lines = listings.map((listing) => {
        const title = getTitleField(listing);
        const description = getDescriptionField(listing);
        const bullets = getBulletFields(listing);
        const faqs = getFaqFields(listing);
        const row = {
            name: listing.name || '',
            sku: listing.sku || '',
            group: listing.superSku || '',
            category: listing.category || '',
            title: title?.text || '',
            title_subgroup: formatFieldSubgroupKey(title?.subgroupMeta),
            description: description?.text || '',
            description_subgroup: formatFieldSubgroupKey(description?.subgroupMeta),
        };

        bullets.forEach((field, index) => {
            row[`bullet_${index + 1}_label`] = field.label || '';
            row[`bullet_${index + 1}`] = field.text || '';
            row[`bullet_${index + 1}_subgroup`] = formatFieldSubgroupKey(field.subgroupMeta);
        });

        faqs.forEach((field, index) => {
            row[`faq_${index + 1}_question`] = field.question || '';
            row[`faq_${index + 1}_answer`] = field.text || '';
            row[`faq_${index + 1}_subgroup`] = formatFieldSubgroupKey(field.subgroupMeta);
        });

        return headers.map((header) => escapeCsvValue(row[header] || '')).join(',');
    });

    return [headers.join(','), ...lines].join('\n');
};

export const formatListingExport = (listing, scope = 'all', vars = []) => {
    const fields =
        scope === 'approved'
            ? listing.fields.filter((field) => field.status === 'approved')
            : listing.fields;

    if (!fields.length) return '';

    const title = fields.find((field) => field.type === 'title');
    const description = fields.find((field) => field.type === 'description');
    const bullets = fields.filter((field) => field.type === 'bullet');
    const faqs = fields.filter((field) => field.type === 'faq');
    const sections = [`${listing.name}${listing.sku ? ` (${listing.sku})` : ''}`];

    if (listing.category) sections[0] += ` - ${listing.category}`;
    if (title?.text) sections.push(`TITLE\n${resolveListingText(title.text, vars)}`);
    if (description?.text) {
        sections.push(`DESCRIPTION\n${resolveListingText(description.text, vars)}`);
    }
    if (bullets.length) {
        sections.push(
            `BULLETS\n${bullets
                .map(
                    (field) =>
                        `- [${field.label}] ${resolveListingText(field.text, vars)}`,
                )
                .join('\n')}`,
        );
    }
    if (faqs.length) {
        sections.push(
            `FAQS\n${faqs
                .map(
                    (field, index) =>
                        `${index + 1}. Q: ${resolveListingText(field.question || '', vars)}\n   A: ${resolveListingText(field.text || '', vars)}`,
                )
                .join('\n\n')}`,
        );
    }

    return sections.join('\n\n');
};

export const formatApprovedExport = (listings, getVarsForListing = () => []) =>
    listings
        .map((listing) =>
            formatListingExport(listing, 'approved', getVarsForListing(listing)),
        )
        .filter(Boolean)
        .join('\n\n\n');

export const downloadTextFile = (filename, text) => {
    if (typeof document === 'undefined') return;

    const blob = new Blob([text], {type: 'text/plain'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

export const copyListingField = async (text, vars = []) =>
    copyText(resolveListingText(text, vars));
